/**
 * Alpine.js component for responsive topic link truncation.
 * Hides whole links (not mid-word) when they overflow the container,
 * showing an ellipsis when truncation occurs.
 */
document.addEventListener('alpine:init', () => {
    Alpine.data('topicLinkList', () => ({
        init() {
            // Cache DOM references (elements don't change after init)
            this.items = Array.from(this.$el.querySelectorAll('.topic-link-item'));
            this.ellipsis = this.$el.querySelector('.topic-link-ellipsis');
            this.srAnnouncement = this.$el.querySelector('.topic-link-sr-announcement');

            // Run initial truncation check after DOM is ready
            this.$nextTick(() => {
                this.calculateVisibleItems();
            });

            // Debounce timeout reference for cleanup
            let resizeTimeout;
            let lastObservedWidth;

            // Recalculate on resize using ResizeObserver with debounce for better performance
            const resizeObserver = new ResizeObserver(entries => {
                const observedWidth = Math.round(entries[0].contentRect.width);
                if (observedWidth === lastObservedWidth) return;
                lastObservedWidth = observedWidth;

                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => this.calculateVisibleItems(), 50);
            });

            // Observe parent element since this component's width is constrained by its container (max-w-full).
            // When parent resizes, available space changes and we need to recalculate visible items.
            // Fallback to this.$el for edge cases where parentElement is null (detached nodes).
            resizeObserver.observe(this.$el.parentElement || this.$el);

            // Register cleanup with Alpine's destroy lifecycle
            this.$el._x_cleanups = this.$el._x_cleanups || [];
            this.$el._x_cleanups.push(() => {
                clearTimeout(resizeTimeout);
                resizeObserver.disconnect();
            });
        },

        /**
         * Calculate which items fit within the container width
         * and hide those that overflow, showing ellipsis if needed.
         * Also updates screen reader announcement for accessibility.
         */
        calculateVisibleItems() {
            if (!this.items.length || !this.ellipsis) return;

            // Get the available width - use container's clientWidth (respects CSS constraints)
            const availableWidth = this.$el.clientWidth;

            // First, show all items and hide ellipsis to measure
            this.items.forEach(item => {
                item.style.display = '';
            });
            this.ellipsis.style.display = 'none';

            // Calculate total content width by summing up individual item widths
            const getContentWidth = () => {
                let width = 0;
                this.items.forEach(item => {
                    if (item.style.display !== 'none') {
                        width += item.offsetWidth;
                    }
                });
                if (this.ellipsis.style.display !== 'none') {
                    width += this.ellipsis.offsetWidth;
                }
                return width;
            };

            // Check if content overflows
            if (getContentWidth() <= availableWidth) {
                this.updateScreenReaderAnnouncement(0);
                return;
            }

            // Show ellipsis to account for its width in calculations
            this.ellipsis.style.display = '';

            // Progressively hide items from the end until content fits
            let currentHiddenCount = 0;
            for (let i = this.items.length - 1; i >= 0; i--) {
                this.items[i].style.display = 'none';
                currentHiddenCount++;

                // Check if content now fits (with some tolerance for rounding)
                if (getContentWidth() <= availableWidth) {
                    break;
                }
            }

            // If all items are hidden, hide the ellipsis too
            if (currentHiddenCount >= this.items.length) {
                this.ellipsis.style.display = 'none';
            }

            // Update screen reader announcement with hidden item count
            this.updateScreenReaderAnnouncement(currentHiddenCount);
        },

        /**
         * Update the screen reader announcement element with the count of hidden items.
         * This provides context to assistive technology users about truncated content.
         */
        updateScreenReaderAnnouncement(hiddenCount) {
            if (!this.srAnnouncement) return;

            if (hiddenCount > 0) {
                // Announce how many additional topics are hidden
                this.srAnnouncement.textContent = hiddenCount === 1
                    ? 'und 1 weiteres Thema'
                    : `und ${hiddenCount} weitere Themen`;
                this.srAnnouncement.style.display = '';
            } else {
                this.srAnnouncement.textContent = '';
                this.srAnnouncement.style.display = 'none';
            }
        }
    }));
});
